"use client";

import { useEffect, useState } from "react";
import { useMediaMatches } from "@/hooks/use-mobile";
import type { US_CITY_POINTS } from "./landing-network-map";

export type DemoCity = keyof typeof US_CITY_POINTS;

export interface DemoScenario {
  request: string;
  from: DemoCity;
  to: DemoCity;
  slug: string;
  product: string;
  productDetail: string;
  productAmount: string;
  freight: string;
  freightAmount: string;
  totalLabel: string;
  totalDetail: string;
  totalAmount: string;
  badge: string;
}

export const DEMO_SCENARIOS: DemoScenario[] = [
  {
    request: "200 heat-treated pallets to Chicago 60612 by Friday",
    from: "memphis",
    to: "chicago",
    slug: "pallets",
    product: "200 heat-treated pallets",
    productDetail: "200 × $14.50",
    productAmount: "$2,900",
    freight: "One truck, 530 mi",
    freightAmount: "$640",
    totalLabel: "Delivered",
    totalDetail: "Chicago, IL 60612 · Thu",
    totalAmount: "$3,540",
    badge: "Quote sent",
  },
  {
    request: "Pick up 340 pallets from my dock in Dallas",
    from: "dallas",
    to: "kansasCity",
    slug: "pallets",
    product: "340 grade B pallets",
    productDetail: "340 × $3.50",
    productAmount: "$1,190",
    freight: "Free pickup, Tue",
    freightAmount: "$0",
    totalLabel: "You get",
    totalDetail: "Paid after pickup",
    totalAmount: "$1,190",
    badge: "Pickup booked",
  },
  {
    request: "260 new GMA pallets to Memphis 38118",
    from: "houston",
    to: "memphis",
    slug: "pallets",
    product: "260 new GMA pallets",
    productDetail: "260 × $9.00",
    productAmount: "$2,340",
    freight: "One truck, 580 mi",
    freightAmount: "$520",
    totalLabel: "Delivered",
    totalDetail: "Memphis, TN 38118 · Wed",
    totalAmount: "$2,860",
    badge: "Quote sent",
  },
  {
    request: "600 grade A pallets to Denver 80216",
    from: "saltLake",
    to: "denver",
    slug: "pallets",
    product: "600 grade A pallets",
    productDetail: "600 × $7.00",
    productAmount: "$4,200",
    freight: "One truck, 520 mi",
    freightAmount: "$780",
    totalLabel: "Delivered",
    totalDetail: "Denver, CO 80216 · Fri",
    totalAmount: "$4,980",
    badge: "Quote sent",
  },
];

const TYPE_MS = 42;
const STEP_MS = 800;
const HOLD_MS = 4000;
export const DEMO_STEPS = 5;

const FIRST_SCENARIO = DEMO_SCENARIOS[0];

function previousIndex(index: number): number {
  return (index + DEMO_SCENARIOS.length - 1) % DEMO_SCENARIOS.length;
}

export function useQuoteDemo() {
  const prefersReducedMotion = useMediaMatches("(prefers-reduced-motion: reduce)");
  const [index, setIndex] = useState(0);
  const [typed, setTyped] = useState(FIRST_SCENARIO?.request.length ?? 0);
  const [step, setStep] = useState(DEMO_STEPS);
  const scenario = DEMO_SCENARIOS[index] ?? FIRST_SCENARIO;
  const requestLength = scenario?.request.length ?? 0;
  const settled = prefersReducedMotion || step >= DEMO_STEPS;
  const sceneIndex = prefersReducedMotion || step >= 1 ? index : previousIndex(index);
  const sceneStep = settled || step === 0 ? DEMO_STEPS : step;
  useEffect(() => {
    if (prefersReducedMotion) return;
    if (typed < requestLength) {
      const timer = window.setTimeout(() => setTyped((count) => count + 1), TYPE_MS);
      return () => window.clearTimeout(timer);
    }
    if (step < DEMO_STEPS) {
      const timer = window.setTimeout(() => setStep((count) => count + 1), STEP_MS);
      return () => window.clearTimeout(timer);
    }
    const timer = window.setTimeout(() => {
      setIndex((current) => (current + 1) % DEMO_SCENARIOS.length);
      setTyped(0);
      setStep(0);
    }, HOLD_MS);
    return () => window.clearTimeout(timer);
  }, [prefersReducedMotion, typed, step, requestLength]);
  return {
    scenario,
    typed: prefersReducedMotion ? requestLength : typed,
    sceneIndex,
    scene: DEMO_SCENARIOS[sceneIndex] ?? scenario,
    previousScene: DEMO_SCENARIOS[previousIndex(sceneIndex)] ?? scenario,
    sceneStep,
    animated: sceneStep < DEMO_STEPS,
  };
}
